import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import cloneDeep from 'lodash/cloneDeep';
import { useIntl } from 'react-intl';
import uuidv4 from 'uuid/v4';

import { ICartEntry, IItem } from '@rbi-ctg/menu';
import { OfferType } from 'generated/rbi-graphql';
import useAuth from 'hooks/auth/use-auth';
import { getPosVendorFromStore } from 'hooks/menu/use-pos-vendor';
import { useNavigation } from 'hooks/navigation/use-navigation';
import { usePriceOrder } from 'hooks/price-order';
import { useCart } from 'hooks/use-cart';
import useDialogModal from 'hooks/use-dialog-modal';
import { useTotalTipCalculations } from 'pages/cart/your-cart/totals/use-total-tip-calculations';
import {
  findEntriesForSwap,
  mapEntryTypeToOfferType,
  replaceEntryForSwap,
} from 'state/cart/helpers';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { useLocale } from 'state/intl';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { isDiscountOffer } from 'state/loyalty/utils';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { ServiceMode, useOrderContext } from 'state/order';
import { useCartTip } from 'state/order/hooks/use-cart-tip';
import { useUnavailableCartEntries } from 'state/order/hooks/use-unavailable-cart-entries';
import { usePaymentContext } from 'state/payment';
import { useStoreContext } from 'state/store';
import { createCartEntry } from 'utils/cart';
import { EventName, emitEvent } from 'utils/event-hub';
import { AttributeName } from 'utils/performance';
import { routes } from 'utils/routing';
import { isDelivery } from 'utils/service-mode';

import { useSanitizedRewards } from './hooks/use-sanitized-rewards';
import type { ICartContext, ICartContextArgs, ISwappableItem, ITryPricingOverrides } from './types';

const CartContext = createContext<ICartContext>({} as ICartContext);

export const CartProvider: React.FC<React.PropsWithChildren<ICartContextArgs>> = ({ children }) => {
  const { navigate } = useNavigation();
  const [cardSuccessfullyAdded, setCardSuccessfullyAdded] = useState(false);

  const [shouldSkipPriceOrder, setShouldSkipPriceOrder] = useState(false);

  const {
    cartVersion,
    calculateCartTotalWithDiscount,
    deliveryAddress,
    deliveryInstructions,
    orderPhoneNumber,
    serviceMode: orderServiceMode,
    selectServiceMode: selectOrderServiceMode,
    setCartEntries: setGlobalCartEntries,
    shouldEmptyCart,
  } = useOrderContext();
  const {
    cartEntries: globalCartEntries,
    emptyCart,
    removeFromCart,
    repriceCartEntries,
  } = useCart();
  const { logEvent } = useMParticleContext();

  const { checkoutPaymentMethod } = usePaymentContext();

  const {
    tipAmount,
    setTipAmount,
    tipSelection,
    setTipSelection,
    updateTipAmount,
    shouldShowTipPercentage,
  } = useCartTip();

  const isFastLaneEnabled = useFlag(LaunchDarklyFlag.ENABLE_FAST_LANE_DRIVE_THRU);
  const isDriveThruHidden = isFastLaneEnabled && orderServiceMode === ServiceMode.DRIVE_THRU;
  const initialServiceMode = isDriveThruHidden ? ServiceMode.TAKEOUT : orderServiceMode;
  const [serviceMode, setServiceMode] = useState<ServiceMode>(initialServiceMode as ServiceMode);
  // Keep local ref of the serviceMode to properly update OrderContext on unmount
  // The ref update is synchronous, while the state update will only occur after re-render
  const contextServiceModeRef = useRef(initialServiceMode);

  const selectServiceMode = useCallback(
    (selectedServiceMode: ServiceMode) => {
      setServiceMode(selectedServiceMode);
      contextServiceModeRef.current = selectedServiceMode;
    },
    [setServiceMode]
  );

  const { user, refreshCurrentUser } = useAuth();
  const { isStoreOpenAndAvailable, store, serviceModeStatus } = useStoreContext();
  const { formatMessage } = useIntl();

  const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const loyaltyAppliedDiscountOffer = useAppSelector(
    selectors.loyalty.selectDiscountAppliedCmsOffer
  );
  const isPricingRewardApplication = useAppSelector(
    selectors.loyalty.selectIsPricingRewardApplication
  );

  const { locale: customerLocale } = useLocale();
  const dispatch = useAppDispatch();

  useEffect(() => {
    // TODO: having a deps array will trigger this cleanup function every time that the deps changes
    // Check if this is intentional or this should only be called on unmount
    return () => {
      if (deliveryAddress?.shouldSave) {
        refreshCurrentUser();
      }
    };
  }, [deliveryAddress, refreshCurrentUser]);

  useEffect(() => {
    // Update OrderContext on unmount since Cart components should use serviceMode from CartContext
    // This significantly improves perceived performance when switching between serviceMode options
    return () => {
      if (contextServiceModeRef.current !== orderServiceMode) {
        selectOrderServiceMode(contextServiceModeRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // Ensure the service mode is being reset if it's Delivery
    // This happens in React Nav when changing stores from the Cart between Pick Up/Delivery that may result in a stale state
    const isDeliveryOrder =
      isDelivery(orderServiceMode) || isDelivery(contextServiceModeRef.current);
    if (isDeliveryOrder && contextServiceModeRef.current !== orderServiceMode) {
      selectServiceMode(orderServiceMode as ServiceMode);
    }
  }, [orderServiceMode, selectServiceMode]);

  const appliedOffersMap = useMemo(
    () =>
      appliedOffers.reduce((acc, offer) => {
        acc[offer.cartId || ''] = offer;
        return acc;
      }, {}),
    [appliedOffers]
  );

  const cartEntries = useMemo(() => {
    // Create the cart entry from the selected discount offer
    // This offer will be available if the cart is not empty
    const selectedOffer = loyaltyAppliedDiscountOffer;

    // @ts-expect-error TS(2322) FIXME: ICartEnty does not support empty vendorConfigs
    const offerDiscountCartEntry: ICartEntry[] =
      globalCartEntries.length && selectedOffer && isDiscountOffer(selectedOffer)
        ? [
            {
              ...createCartEntry({ item: selectedOffer }),
              cartId: 'discount-offer',
              vendorConfigs: undefined,
              offerVendorConfigs: selectedOffer.vendorConfigs,
            },
          ]
        : [];

    // Updating entry type if its part of an applied offer
    const updatedCartEntries = globalCartEntries.map((entry: ICartEntry) =>
      appliedOffersMap[entry.cartId]
        ? { ...entry, type: mapEntryTypeToOfferType(entry.type) }
        : entry
    );

    // Cart entries with offers
    return repriceCartEntries(updatedCartEntries.concat(offerDiscountCartEntry));
  }, [appliedOffersMap, loyaltyAppliedDiscountOffer, globalCartEntries, repriceCartEntries]);

  const rewardsApplied = useSanitizedRewards({ cartEntries });

  const swapItems = useCallback(
    (to: IItem, swapOffer: ISwappableItem) => {
      const { from, ...offer } = swapOffer;
      setGlobalCartEntries((prevEntries: ICartEntry[]) => {
        // Root cart entry is needed for appliedOffers
        // Child cart entry is needed for swaps objects
        const { rootEntry, childEntry } = findEntriesForSwap(prevEntries, from);
        let entriesToReturn = prevEntries;

        if (rootEntry) {
          const oldEntry = rootEntry;
          // Define price for item
          const oldEntryUnitaryPrice = (oldEntry?.price ?? 0) / oldEntry.quantity;
          const { type: swapType, offerId, offerType, cmsId } = offer;

          const entryToSwap = {
            ...createCartEntry({
              item: to,
              price: oldEntry.children.length ? 0 : oldEntryUnitaryPrice,
              quantity: 1,
            }),
            sanityId: to._id,
          };

          const offerToApply = {
            id: offerId,
            cartId: oldEntry.children.length ? oldEntry.cartId : entryToSwap.cartId,
            type: OfferType[offerType],
            swap: {
              cartId: entryToSwap.cartId,
              from,
              to: to._id,
              swapType,
            },
            cmsId,
          };

          // This covers the case when a cartEntry has more than one item and swap is selected
          // updating price and quantity to the cartEntry that was swapped
          if (oldEntry.quantity > 1) {
            const newQuantity = oldEntry.quantity - 1;
            const newPrice = oldEntryUnitaryPrice * newQuantity;
            // If cartEntry has children, we keep a copy of the entry without
            // the swap applied to concat to the entries array after applying the swap
            if (oldEntry.children.length) {
              const splittedItem = cloneDeep(oldEntry);
              splittedItem.cartId = uuidv4();
              splittedItem.quantity = newQuantity;
              splittedItem.price = newPrice;
              offerToApply.cartId = oldEntry.cartId;
              // Creating new cart entry with swap applied to replace at root level
              // having quantity and price correct for a swapped item
              const newRoot = {
                ...oldEntry,
                children: replaceEntryForSwap(oldEntry.children, from, {
                  ...entryToSwap,
                  price: 0,
                }),
                price: oldEntryUnitaryPrice,
                quantity: 1,
              };

              entriesToReturn = replaceEntryForSwap(prevEntries, oldEntry._id, newRoot).concat(
                splittedItem
              );
            } else {
              entriesToReturn = replaceEntryForSwap(prevEntries, oldEntry._id, {
                ...oldEntry,
                quantity: newQuantity,
                price: newPrice,
              }).concat(entryToSwap);
            }
          } else {
            entriesToReturn = replaceEntryForSwap(prevEntries, from, entryToSwap);
          }

          dispatch(actions.loyalty.applyOffer(offerToApply));

          logEvent(CustomEventNames.SWAP_OFFER_USED, EventTypes.Other, {
            'Original Item Name': childEntry?.name,
            'Original Item Sanity ID': from,
            'Swapped Item Name': to.name?.locale,
            'Swapped Item Sanity ID': to._id,
            'Systemwide Offer ID': offer.offerId,
          });
        }

        return entriesToReturn;
      });
    },
    [setGlobalCartEntries, dispatch, logEvent]
  );
  const {
    priceOrder,
    orderStatus,
    priceOrderFailure,
    priceOrderSuccess,
    priceResult: { error: priceMutationError },
    serverOrder,
  } = usePriceOrder({
    cartEntries,
    updateTipAmount,
  });

  const restaurantPosDataId = store.restaurantPosData?._id ?? '';
  const storeNumber = store.number ?? '';
  const vendor = getPosVendorFromStore(store, serviceMode);
  const { unavailableCartEntries, setUnavailableCartEntries } = useUnavailableCartEntries({
    cartEntries,
    serverOrder,
    restaurantPosDataId,
    storeNumber,
    vendor,
  });

  const { rewardDiscountAmount = 0, additionalDiscountAmount = 0 } = useTotalTipCalculations({
    serverOrder,
    serviceMode,
  });

  const onConfirmRemoveItemDialog = useCallback(
    (entry: ICartEntry | null | undefined) => {
      if (!entry) {
        return;
      }
      emitEvent(EventName.CART_CONTENT_UPDATE, {
        attributes: [
          {
            name: AttributeName.CART_UPDATE_SOURCE,
            value: 'remove_item',
          },
        ],
      });
      return shouldEmptyCart(entry) ? emptyCart() : removeFromCart(entry);
    },
    [removeFromCart, shouldEmptyCart, emptyCart]
  );

  const [RemoveItemDialog, openRemoveItemDialog, itemToRm] = useDialogModal<ICartEntry>({
    onConfirm: onConfirmRemoveItemDialog,
    showCancel: true,
    modalAppearanceEventMessage: 'Confirmation: Remove item from cart',
  });

  // for confirming item removal
  const confirmRemoveItemFromCart = useCallback(
    (cartId: string) => {
      const entryToRemove = globalCartEntries.find((item: ICartEntry) => cartId === item.cartId);
      if (entryToRemove) {
        openRemoveItemDialog(entryToRemove);
      }
    },
    [globalCartEntries, openRemoveItemDialog]
  );

  const { cartTotal, isCartTotalNegative } = calculateCartTotalWithDiscount(cartEntries);
  const totalAppliedDiscount = rewardDiscountAmount + additionalDiscountAmount;

  // Reward discounts are already applied to items causing the requested amount to be less than the real amount since
  // We are substracting the discount twice (on the item and again on the discount)
  const requestedAmountCents = cartTotal - totalAppliedDiscount + rewardDiscountAmount;

  const customerName =
    isFastLaneEnabled && serviceMode === ServiceMode.TAKEOUT ? `FL-${user?.details?.name}` : null;

  const tryPricing = useCallback(
    (overrideOrderParams?: ITryPricingOverrides) => {
      if (!isStoreOpenAndAvailable || !user || isCartTotalNegative || !serviceMode) {
        return;
      }

      const validAppliedOffers = appliedOffers.map(({ id, cartId, swap, type, cmsId }) => ({
        id,
        cartId,
        swap,
        type,
        sanityId: cmsId,
      }));

      const orderParams = {
        rewardsApplied,
        cartVersion,
        customerLocale,
        customerName,
        deliveryAddress: deliveryAddress
          ? {
              ...deliveryAddress,
              phoneNumber: orderPhoneNumber,
            }
          : null,
        orderPhoneNumber,
        deliveryInstructions,
        appliedOffers: validAppliedOffers,
        requestedAmountCents,
        serviceMode,
        store,
        isStoreOpenAndAvailable,
        ...overrideOrderParams,
      };

      void priceOrder(orderParams, user);
      if (isPricingRewardApplication) {
        dispatch(actions.loyalty.setIsPricingRewardApplication(false));
      }
    },
    [
      additionalDiscountAmount,
      rewardsApplied,
      appliedOffers,
      calculateCartTotalWithDiscount,
      cartVersion,
      checkoutPaymentMethod,
      customerLocale,
      customerName,
      deliveryAddress,
      deliveryInstructions,
      dispatch,
      isPricingRewardApplication,
      isStoreOpenAndAvailable,
      orderPhoneNumber,
      priceOrder,
      rewardDiscountAmount,
      serviceMode,
      store,
      user,
      requestedAmountCents,
    ]
  );

  const removeItemMessage = (item: ICartEntry | null) => {
    return item
      ? formatMessage(
          { id: 'removeItemFromCart' },
          {
            item: item.name,
            offerText: '',
          }
        )
      : '';
  };

  const pricedAndAvailable = useMemo(() => {
    return priceOrderSuccess && !unavailableCartEntries.length && !priceMutationError;
  }, [priceMutationError, priceOrderSuccess, unavailableCartEntries.length]);

  const enableFutureOrdering = useFlag(LaunchDarklyFlag.ENABLE_FUTURE_ORDERING);

  const selectedDonationCartEntry = useMemo(
    () => cartEntries.find((cartEntry: ICartEntry) => cartEntry.isDonation),
    [cartEntries]
  );

  // Navigate to Home if cart is emptied out by any reason
  useEffect(() => {
    if (!globalCartEntries?.length) {
      navigate(routes.base);
    }
  }, [navigate, globalCartEntries]);

  return (
    <CartContext.Provider
      value={{
        confirmRemoveItemFromCart,
        serverOrder,
        tryPricing,
        cardSuccessfullyAdded,
        setCardSuccessfullyAdded,
        orderStatus,
        priceOrderFailure,
        priceOrderSuccess,
        priceMutationError,
        pricedAndAvailable,
        unavailableCartEntries,
        setUnavailableCartEntries,
        serviceMode,
        selectServiceMode,
        serviceModeStatus,
        shouldSkipPriceOrder,
        setShouldSkipPriceOrder,
        enableFutureOrdering,
        selectedDonationCartEntry,
        swapItems,
        requestedAmountCents,
        tipAmount,
        setTipAmount,
        tipSelection,
        setTipSelection,
        updateTipAmount,
        shouldShowTipPercentage,
      }}
    >
      {children}

      <RemoveItemDialog
        body={removeItemMessage(itemToRm)}
        heading={formatMessage({ id: 'removeItem' })}
      />
    </CartContext.Provider>
  );
};

export const useCartContext = () => useContext(CartContext);
